import type {TranslateConfig} from './index';
import type {ExtractConfig} from './commands/extract';
import type {ConfigDefaults} from './utils/config';
import type {Config} from '~/core/config';
import type {Toc} from '~/core/toc';

import {dirname, extname, join, resolve} from 'node:path';
import {isMainThread} from 'node:worker_threads';

import {TocService} from '~/core/toc';
import {Run as BaseRun} from '~/core/run';
import {normalizePath} from '~/core/utils';
import {VarsService} from '~/core/vars';
import {MetaService} from '~/core/meta';
import {MarkdownService} from '~/core/markdown';

import {FileLoader, resolveFiles} from './utils';

type CommonRunConfig = Omit<TranslateConfig, 'provider' | 'timeout' | 'copyAssets'> &
    ExtractConfig &
    ConfigDefaults;

export type RunOptions = {
    /** Apply presets.yaml to conditions, as build does (`--presets`). Off by default. */
    usePresets?: boolean;
};

export type GetFilesOptions = {
    /**
     * Whether the caller loads tocs through `getFileContent`, which inlines
     * `include`d tocs (link and merge modes) into their parent. Then the
     * included tocs must not be listed on their own: their strings are
     * already part of the parent, and `TocService.for()` throws for them.
     *
     * Callers that read files as they are on disk (`translate`, `seed`)
     * leave this off: the parent toc keeps its `include` entry, so the
     * included toc needs a translation of its own.
     */
    inlinedTocs?: boolean;
};

/**
 * Presets as the build of the translation sees them: every lookup - tocs and
 * their merges, the includes that `translate.filter` follows, the content -
 * takes the presets on the path of the target file (`ru/x.md` -> `en/x.md`).
 * The presets of the source describe the source build: its `lang: ru` would
 * keep the Russian branch of `{% if lang == "ru" %}` in the English page, and
 * a lookup left on the source would pick other files than the content keeps.
 */
class TranslationVarsService extends VarsService {
    private readonly languages: {source: string; target: string};

    constructor(run: Run, languages: {source: string; target: string}) {
        super(run);

        this.languages = languages;
    }

    for(path: RelativePath, from?: NormalizedPath) {
        const {source, target} = this.languages;

        return super.for(path, languagePath(normalizePath(from || path), source, target));
    }
}

/**
 * The path of a file in the target language directory, as `languageRepath`
 * places the translation; a file outside a source language directory (the
 * input is that directory itself) keeps its path.
 */
function languagePath(file: NormalizedPath, source: string, target: string) {
    const parts = file.split('/');
    const index = parts.slice(0, -1).indexOf(source);

    if (index === -1) {
        return file;
    }

    parts[index] = target;

    return parts.join('/') as NormalizedPath;
}

export class Run extends BaseRun<CommonRunConfig> {
    readonly vars: VarsService;
    readonly meta: MetaService;
    readonly toc: TocService;
    readonly markdown: MarkdownService;
    readonly tocYamlList: Set<NormalizedPath>;

    constructor(config: Config<CommonRunConfig>, {usePresets = false}: RunOptions = {}) {
        super(config);

        this.scopes.set('input', this.realpathSync(config.input));
        this.scopes.set('output', this.realpathSync(config.output));
        const sourcePath = join(config.input, config.source.language) as AbsolutePath;
        this.scopes.set('source', this.realpathSync(sourcePath));

        // With `--presets`, vars apply as for the build of the translation:
        // the `varsPreset` section of every presets.yaml on the path of the
        // target file, under `--vars`, so the content that the build drops
        // does not go to translation either. Off by default and never on for
        // extract: the XLIFF for external tools takes its variables from
        // `--vars` only.
        this.vars = usePresets
            ? new TranslationVarsService(this, {
                  source: config.source.language,
                  // One target per run with presets, see `checkPresetsTargets`.
                  target: config.target[0].language,
              })
            : new VarsService(this, {usePresets: false});
        this.meta = new MetaService(this);
        this.toc = new TocService(this, {skipMissingVars: true, mode: 'translate'});
        this.markdown = new MarkdownService(this, {skipMissingVars: true, mode: 'translate'});
        this.tocYamlList = new Set<NormalizedPath>();
    }

    async prepareRun() {
        await this.vars.init();
        await this.markdown.init();

        if (isMainThread) {
            const paths = await this.glob('**/toc.yaml', {
                cwd: this.input,
            });

            await this.toc.init(paths);

            for (const toc of paths) {
                this.tocYamlList.add(toc);
            }

            await this.toc.init(Array.from(this.tocYamlList) as NormalizedPath[]);
        }
    }

    async getFiles({inlinedTocs = false}: GetFilesOptions = {}): Promise<
        [string[], [string, string][]]
    > {
        const allFiles = new Set<NormalizedPath>();
        const copiedFromPaths = new Set<NormalizedPath>();
        const mergedDirectories = new Set<NormalizedPath>();

        for (const entry of this.toc.entries) {
            const metadata = this.meta.get(entry);
            if (metadata.sourcePath) {
                const sourcePath = metadata.sourcePath as NormalizedPath;
                copiedFromPaths.add(sourcePath);

                const sourcePathWithLang = normalizePath(
                    join(this.config.source.language, sourcePath),
                );
                copiedFromPaths.add(sourcePathWithLang);

                const sourceDir = normalizePath(dirname(sourcePath));
                mergedDirectories.add(sourceDir);
                const sourceDirWithLang = normalizePath(
                    join(this.config.source.language, sourceDir),
                );
                mergedDirectories.add(sourceDirWithLang);
            }
        }

        for (const entry of this.toc.entries) {
            if (!copiedFromPaths.has(entry)) {
                allFiles.add(entry);
            }
        }

        for (const entry of this.toc.entries) {
            if (copiedFromPaths.has(entry)) {
                continue;
            }

            try {
                const deps = await this.markdown.deps(entry);

                for (const dep of deps) {
                    if (dep.path.endsWith('.md') && !copiedFromPaths.has(dep.path)) {
                        allFiles.add(dep.path);
                    }
                }
            } catch (error) {
                this.logger.warn(error);
                allFiles.delete(entry);
            }
        }

        const filteredTocYamlList = Array.from(this.tocYamlList).filter((toc) => {
            if (copiedFromPaths.has(toc)) {
                return false;
            }

            const tocDir = normalizePath(dirname(toc));
            if (mergedDirectories.has(tocDir)) {
                return false;
            }

            // Tocs consumed by an include (link or merge mode) are not registered as
            // standalone toc nodes. When the caller inlines includes, their strings
            // are already extracted with the parent. See the same guard below in
            // `finalFiles`.
            if (inlinedTocs && !this.toc.isToc(toc)) {
                return false;
            }

            return true;
        });

        const allFilesArray = Array.from([...allFiles, ...filteredTocYamlList]);

        const [resolvedFiles, skipped] = resolveFiles(
            this.config.input,
            this.config.files,
            this.config.include,
            this.config.exclude,
            this.config.source.language,
            ['.md', '.yaml'],
            this.config.filter ? allFilesArray : null,
        );

        const finalFiles: string[] = resolvedFiles.filter((file) => {
            const normalizedFile = normalizePath(file);

            if (copiedFromPaths.has(normalizedFile)) {
                return false;
            }

            // Tocs consumed by an include are not registered as standalone toc nodes
            // (they remain `source` graph nodes). When the caller inlines includes
            // (`extract`), their strings are already extracted with the parent toc, so
            // extracting them on their own both duplicates content and throws
            // `Error while finding toc dir.` in TocService.for(). This is the primary
            // guard: it runs for both the default and `--filter` file lists.
            if (
                inlinedTocs &&
                this.tocYamlList.has(normalizedFile) &&
                !this.toc.isToc(normalizedFile)
            ) {
                return false;
            }

            for (const mergedDir of mergedDirectories) {
                if (normalizedFile.startsWith(mergedDir + '/') || normalizedFile === mergedDir) {
                    return false;
                }
            }

            return true;
        });

        return [finalFiles, skipped] as [string[], [string, string][]];
    }

    async getFileContent(file: NormalizedPath) {
        const type = extname(file).slice(1);

        if (type === 'md') {
            return this.markdown.load(file);
        }

        if (this.tocYamlList.has(file)) {
            const toc: Partial<Toc> = this.toc.for(file);

            delete toc.path;

            return toc;
        }

        const path = resolve(this.config.input, file);

        const loader = new FileLoader(path, this.config.refResolve);

        return loader.load();
    }
}
